import { Divider, Stack } from 'component-library';
import { FC, HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { ConservationBenchmarkType, TrendLabel as TrendLabelEnum } from '@/api/rest/resources/conservation';
import { ChartLegendBar } from '@/components/Charts';
import { ChartLegendBarItem } from '@/components/Charts/components/types';
import { ChartProps } from '@/components/Charts/types';
import { minMaxAxisDomain } from '@/components/Charts/utils';
import { BenchmarkPill } from '@/components/Pill/ConservationPill';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { useGetConservationBenchmarks } from '@/pages/shared/projects/pages/project/pages/conservation/BU/hooks/useGetConservationBenchmarks';
import { getBenchmarkForConservationFact } from '@/pages/shared/projects/pages/project/pages/conservation/BU/utils/getBenchmarkForConservationFact';
import {
  Bar,
  Chart,
  ChartTooltipTextPrimary,
  ChartTooltipTextSecondary,
  ComposedChart,
  TooltipProps,
  useTimeseriesXAxisProps,
} from '@/pages/shared/projects/pages/project/pages/conservation/shared/components/Chart';
import {
  RenderBenchmarkReferenceLine,
  RenderTrendLine,
  TrendLabel,
} from '@/pages/shared/projects/pages/project/pages/conservation/shared/components/ReferenceLine';
import { isNullish } from '@/utils/booleans';
import { printYear } from '@/utils/formatting/date';

enum Indicators {
  ei = 'ecologicalIntegrity',
  hi = 'habitatIntactness',
  is = 'indicatorSpecies',
}

export { Indicators as EcologicalIntegrityIndicators };

const chartColors = {
  [Indicators.ei]: { stroke: '#276B50', dot: '#474D45' },
  [Indicators.hi]: { fill: '#A9BD89' },
  [Indicators.is]: { fill: '#BC9EE3' },
} as const;

export type EcologicalIntegrityChartData = {
  [Indicators.ei]: number | null;
  [Indicators.hi]: number | null;
  [Indicators.is]: number | null;
  date: Date;
  benchmark: number | null;
};

type EcologicalIntegrityChart = HTMLAttributes<HTMLDivElement> & {
  data: EcologicalIntegrityChartData[];
  benchmark: ConservationBenchmarkType | null;
  trendLabel: TrendLabelEnum | null;
  yAxisUpperBound?: number;
} & Omit<ChartProps, 'data'>;

export const EcologicalIntegrityHistoricalChart: FC<EcologicalIntegrityChart> = ({
  data,
  benchmark,
  trendLabel,
  yAxisUpperBound,
  ...delegated
}) => {
  const { t } = useTranslation();

  const xAxisProps = useTimeseriesXAxisProps(data);
  const chartConfig = useGetChartElementsConfig();
  const benchmarks = useGetConservationBenchmarks(yAxisUpperBound);

  return (
    <>
      <Chart.Container
        {...delegated}
        data={data}
        config={chartConfig}
        data-testid='ecological-integrity-historical-chart'
      >
        <ComposedChart data={data}>
          <Bar
            isAnimationActive={false}
            fill={chartColors[Indicators.hi].fill}
            dataKey={Indicators.hi}
            barSize={20}
            className={`${Indicators.hi}-bar`}
          />
          <Bar
            isAnimationActive={false}
            fill={chartColors[Indicators.is].fill}
            dataKey={Indicators.is}
            barSize={20}
            className={`${Indicators.is}-bar`}
          />
          <Chart.Tooltip content={(props) => <CustomTooltip benchmarks={benchmarks} {...props} />} />
          {RenderTrendLine({
            dataKey: Indicators.ei,
            stroke: chartColors[Indicators.ei].stroke,
            label: (props) =>
              props.index === data.length - 1 && trendLabel ? (
                <TrendLabel
                  trend={trendLabel}
                  lineColor={chartColors[Indicators.ei].stroke}
                  strokeWidth={2}
                  infoPopoverProps={{
                    title: t(`shared.projects.project.conservation.trendLabel.${trendLabel}`),
                    body: t(`shared.projects.project.conservation.explainers.tooltip.trendLabel.${trendLabel}`),
                  }}
                  {...props}
                />
              ) : (
                <></>
              ),
            dotFill: chartColors[Indicators.ei].dot,
          })}
          {benchmark &&
            RenderBenchmarkReferenceLine({
              value: benchmarks[`${benchmark}`],
              benchmark,
              infoPopoverProps: {
                title: t(`shared.projects.project.conservation.benchmark.${benchmark}`),
                body: t(`shared.projects.project.conservation.explainers.tooltip.benchmark.${benchmark}`),
              },
            })}
          <Chart.XAxis {...xAxisProps} padding={{ left: 40, right: 100 }} className='ei-x-axis' />
          <Chart.YAxis
            domain={minMaxAxisDomain(0.98, 1.02, 0, yAxisUpperBound)}
            tickFormatter={yAxisUpperBound === 1 ? (value: number) => value.toFixed(2) : undefined}
            className='ei-y-axis'
          >
            <Chart.YAxisLabel>{t('shared.ncaDetail.ecologicalIntegrity.historicalDataBU.value')}</Chart.YAxisLabel>
          </Chart.YAxis>
        </ComposedChart>
      </Chart.Container>
      <CustomLegendBar />
    </>
  );
};

const useGetChartElementsConfig = () => {
  const { t } = useTranslation();

  return {
    [Indicators.ei]: {
      label: t(`shared.ncaDetail.ecologicalIntegrity.historicalDataBU.attributeLabel.${Indicators.ei}`),
      color: chartColors[Indicators.ei].dot,
    },
    [Indicators.hi]: {
      label: t(`shared.ncaDetail.ecologicalIntegrity.historicalDataBU.attributeLabel.${Indicators.hi}`),
      color: chartColors[Indicators.hi].fill,
    },
    [Indicators.is]: {
      label: t(`shared.ncaDetail.ecologicalIntegrity.historicalDataBU.attributeLabel.${Indicators.is}`),
      color: chartColors[Indicators.is].fill,
    },
  };
};

const CustomTooltip = ({
  active,
  payload,
  label,
  benchmarks,
}: TooltipProps<ValueType, NameType> & { benchmarks: Record<ConservationBenchmarkType, number> }) => {
  const { t } = useTranslation();

  if (!active || !payload?.length) return null;

  const indicators = (Object.keys(Indicators) as Array<keyof typeof Indicators>).map((indicatorKey) => {
    const indicator = Indicators[`${indicatorKey}`];
    const indicatorPayload = payload.find((data) => data.dataKey === indicator);

    const indicatorDisplayValue = !isNullish(indicatorPayload?.value)
      ? getDisplayNumber(indicatorPayload?.value as string, window.navigator.language)
      : t('global.analysis.noData');

    return {
      indicator,
      indicatorLabel: t(`shared.ncaDetail.ecologicalIntegrity.historicalDataBU.attributeLabel.${indicator}`),
      value: indicatorDisplayValue,
    };
  });

  const eiIndicator = payload.find((data) => data.dataKey === Indicators.ei);

  const benchmark = getBenchmarkForConservationFact({
    fact: { value: eiIndicator?.value as number | null },
    benchmarks,
  });

  return (
    <Stack direction='col' spacing={1.5} className='rounded-md bg-white-100 px-2 py-1 elevation-1'>
      <Stack spacing={1}>
        <Stack spacing={2}>
          <ChartTooltipTextSecondary>{printYear(label)}</ChartTooltipTextSecondary>
          {benchmark && <BenchmarkPill className='typography-body2' benchmark={benchmark} />}
        </Stack>
        {indicators.map(({ indicatorLabel, value, indicator }, index) => {
          const TextComponent = indicator === Indicators.ei ? ChartTooltipTextPrimary : ChartTooltipTextSecondary;

          return (
            <div key={`${indicator}-${index}`} className='my-1'>
              <Stack spacing={8} direction='row' className='justify-between'>
                <TextComponent>{indicatorLabel}</TextComponent>
                <TextComponent>{value}</TextComponent>
              </Stack>
              {index !== indicators.length - 1 && <Divider className='mt-2' />}
            </div>
          );
        })}
      </Stack>
    </Stack>
  );
};

export { CustomTooltip as CustomTooltipEcologicalIntegrity };

const CustomLegendBar = () => {
  const { t } = useTranslation();
  const chartConfig = useGetChartElementsConfig();

  const legendBarItems: ChartLegendBarItem[] = [
    {
      ...chartConfig[Indicators.ei],
    },
    {
      label: t('shared.ncaDetail.ecologicalIntegrity.historicalDataBU.trend'),
      color: chartColors[Indicators.ei].stroke,
      shape: 'line',
    },
    {
      ...chartConfig[Indicators.hi],
      shape: 'column',
    },
    {
      ...chartConfig[Indicators.is],
      shape: 'column',
    },
  ];

  return (
    <ChartLegendBar
      items={legendBarItems}
      className='sm:ml-[28px]'
      data-testid='ecological-integrity-historical-chart-legend'
    />
  );
};
